import type {
  EventBusMessage,
  EventBusRequest,
  EventBusResponse,
} from '../shared/eventBus';
import { API_HOST } from '../config/apiConfig';
import { buildJsonHeaders } from './assistantAuth';

const DEFAULT_STREAM_API_PATH = '/api/stream';

const buildStreamApiUrl = (path: string): string => {
  if (!path) {
    return `${API_HOST}${DEFAULT_STREAM_API_PATH}`;
  }
  if (path.startsWith('?') || path.startsWith('#')) {
    return `${API_HOST}${DEFAULT_STREAM_API_PATH}${path}`;
  }
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${API_HOST}${DEFAULT_STREAM_API_PATH}${suffix}`;
};

const buildStreamHeaders = (): Record<string, string> => {
  const headers = buildJsonHeaders();
  delete headers['Content-Type'];
  headers.Accept = 'text/event-stream';
  return headers;
};

export async function publishEventBusEvent(
  params: EventBusRequest
): Promise<EventBusResponse> {
  if (typeof window !== 'undefined') {
    console.info('event-bus: send', params);
  }
  const response = await fetch(buildStreamApiUrl(''), {
    method: 'POST',
    headers: buildJsonHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let message = 'Event bus request failed.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return (await response.json()) as EventBusResponse;
}

/**
 * POST against `/api/stream/sync` — backend's synchronous endpoint that
 * returns the `EventBusMessage` reply inline (no SSE round-trip needed).
 * Used by the scene_open flow to avoid the slow SSE handshake on some
 * mobile browsers. See `state/sceneOpen.ts`.
 */
export async function publishEventBusEventSync(
  params: EventBusRequest,
  signal?: AbortSignal,
): Promise<EventBusMessage> {
  const response = await fetch(buildStreamApiUrl('/sync'), {
    method: 'POST',
    headers: buildJsonHeaders(),
    body: JSON.stringify(params),
    signal,
  });
  if (!response.ok) {
    let message = 'Event bus sync request failed.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // fall back to generic message
    }
    throw new Error(message);
  }
  return (await response.json()) as EventBusMessage;
}

type EventBusStreamHandlers = {
  /**
   * Fired AFTER `fetch()` resolves with `response.ok === true` — i.e.
   * when the HTTP request to `/api/stream?sessionId=...` has succeeded
   * and `response.body` is a live ReadableStream. The SSE subscription
   * is live at this point; backend can deliver events into it.
   *
   * Distinct from `onSession` which fires only when the backend sends
   * a payload of shape `{sessionId}` without an `event` field — some
   * backends never send that and only emit `event`-bearing payloads, so
   * `onSession` may never fire. `onConnected` is the reliable signal
   * for "we're subscribed".
   */
  onConnected?: () => void;
  onMessage: (message: EventBusMessage) => void;
  onSession?: (sessionId: string) => void;
  onError?: (error: Error) => void;
};

const parseEventStream = async (
  response: Response,
  handlers: EventBusStreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  if (!response.body) {
    throw new Error('Event stream body is empty.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length === 0) {
      return;
    }
    const data = dataLines.join('\n');
    dataLines = [];
    try {
      const parsed = JSON.parse(data) as Partial<EventBusMessage> & {
        sessionId?: string;
      };
      if (parsed && parsed.event) {
        if (typeof window !== 'undefined') {
          console.info('event-bus: receive', parsed);
        }
        handlers.onMessage(parsed as EventBusMessage);
        return;
      }
      if (parsed?.sessionId && handlers.onSession) {
        if (typeof window !== 'undefined') {
          console.info('event-bus: session', parsed.sessionId);
        }
        handlers.onSession(parsed.sessionId);
      }
    } catch (error) {
      if (handlers.onError) {
        handlers.onError(
          error instanceof Error ? error : new Error('Failed to parse SSE payload.')
        );
      }
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    if (signal?.aborted) {
      try {
        reader.cancel();
      } catch {
        // ignore
      }
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    let lineBreakIndex = buffer.indexOf('\n');
    while (lineBreakIndex !== -1) {
      const line = buffer.slice(0, lineBreakIndex).replace(/\r$/, '');
      buffer = buffer.slice(lineBreakIndex + 1);

      if (!line) {
        dispatch();
        lineBreakIndex = buffer.indexOf('\n');
        continue;
      }

      if (line.startsWith(':')) {
        lineBreakIndex = buffer.indexOf('\n');
        continue;
      }

      const [field, ...rest] = line.split(':');
      const valuePart = rest.join(':').trimStart();

      if (field === 'event') {
        void valuePart;
      } else if (field === 'data') {
        dataLines.push(valuePart);
      }

      lineBreakIndex = buffer.indexOf('\n');
    }
  }
};

export async function subscribeEventBus(
  sessionId: string,
  handlers: EventBusStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    buildStreamApiUrl(`?sessionId=${encodeURIComponent(sessionId)}`),
    {
      method: 'GET',
      headers: buildStreamHeaders(),
      signal,
    }
  );

  if (!response.ok) {
    let message = 'Failed to connect to event stream.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  // Connection established — fetch resolved with ok status and a live
  // ReadableStream `response.body`. Fire BEFORE parseEventStream starts
  // (parser is an awaited promise that resolves only when the stream
  // closes — would never let us signal "live" in time).
  handlers.onConnected?.();

  await parseEventStream(response, handlers, signal);
}
